import { useEffect, useRef, useState } from "react";
import { IconClose, IconDownload, IconShare } from "./icons";
import { downloadFile, shareFile } from "./share";
import { composeShareImage, type ShareFormat } from "./shareCompose";

interface ShareSheetProps {
  faceUrl: string;
  selfieUrl: string | null;
  onClose: () => void;
  onToast: (message: string) => void;
}

const FOCUSABLE_SELECTOR =
  'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function ShareSheet({ faceUrl, selfieUrl, onClose, onToast }: ShareSheetProps) {
  const sheetRef = useRef<HTMLDivElement>(null);
  // onClose is recreated each parent render; read the latest from a ref so the
  // focus-trap effect only runs once (mount/unmount) instead of rebinding.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  const [format, setFormat] = useState<ShareFormat>("square");
  const [showOriginal, setShowOriginal] = useState<boolean>(selfieUrl !== null);
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [rendering, setRendering] = useState<boolean>(true);

  useEffect(() => {
    const sheet = sheetRef.current;
    if (!sheet) return;

    const previouslyFocused = document.activeElement as HTMLElement | null;
    sheet.focus();

    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onCloseRef.current();
        return;
      }
      if (event.key !== "Tab") return;

      const focusable = sheet.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement as HTMLElement | null;

      if (event.shiftKey) {
        if (active === first || !sheet.contains(active)) {
          event.preventDefault();
          last.focus();
        }
      } else {
        if (active === last || !sheet.contains(active)) {
          event.preventDefault();
          first.focus();
        }
      }
    };

    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("keydown", handleKey);
      previouslyFocused?.focus();
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    let objectUrl: string | null = null;
    setRendering(true);
    void composeShareImage({ faceUrl, selfieUrl, format, showOriginal }).then((composed) => {
      if (cancelled) return;
      setFile(composed);
      if (composed) {
        objectUrl = URL.createObjectURL(composed);
        setPreview(objectUrl);
      } else {
        setPreview(null);
      }
      setRendering(false);
    });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [faceUrl, selfieUrl, format, showOriginal]);

  async function handleShare() {
    if (!file) return;
    const outcome = await shareFile(file);
    if (outcome === "copied") onToast("링크를 복사했어요");
    else if (outcome === "unsupported") onToast("공유를 지원하지 않는 환경이에요");
    else onClose();
  }

  function handleSave() {
    if (!file) return;
    downloadFile(file);
    onToast("이미지를 저장했어요");
  }

  return (
    <div className="sheet-backdrop" onClick={onClose}>
      <div
        ref={sheetRef}
        className="sheet"
        role="dialog"
        aria-modal="true"
        aria-label="공유 이미지 만들기"
        tabIndex={-1}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="sheet-head">
          <h2>공유 이미지 만들기</h2>
          <button type="button" className="sheet-close" onClick={onClose} aria-label="닫기">
            <IconClose size={20} />
          </button>
        </div>

        <div className={`sheet-preview ${format}`}>
          {preview ? (
            <img src={preview} alt="공유 미리보기" className="sheet-img" />
          ) : (
            <div className="sheet-img placeholder" />
          )}
          {rendering && (
            <div className="sheet-rendering" aria-hidden>
              <div className="spinner" />
            </div>
          )}
        </div>

        <div className="sheet-options">
          <div className="seg" role="group" aria-label="구성">
            <button
              type="button"
              data-active={showOriginal}
              onClick={() => setShowOriginal(true)}
              disabled={selfieUrl === null}
            >
              원본 + 새 얼굴
            </button>
            <button
              type="button"
              data-active={!showOriginal}
              onClick={() => setShowOriginal(false)}
            >
              새 얼굴만
            </button>
          </div>
          <div className="seg" role="group" aria-label="형식">
            <button
              type="button"
              data-active={format === "square"}
              onClick={() => setFormat("square")}
            >
              정사각
            </button>
            <button
              type="button"
              data-active={format === "story"}
              onClick={() => setFormat("story")}
            >
              스토리
            </button>
          </div>
        </div>

        <div className="sheet-actions">
          <button
            type="button"
            className="spin-btn"
            disabled={!file}
            onClick={() => void handleShare()}
          >
            <IconShare size={20} />
            <span>공유하기</span>
          </button>
          <button type="button" className="secondary-btn" disabled={!file} onClick={handleSave}>
            <IconDownload size={18} />
            <span>저장</span>
          </button>
        </div>
      </div>
    </div>
  );
}
